/**
 * Rango Quantum — motor de cálculo (port 1:1 a JS de
 * backend/research/rango_quantum/signals.py::compute_signal_frame).
 *
 * Igual que pineIndicators.js: cualquier cambio de lógica se hace primero en
 * signals.py (validado contra Pine, 0.2 del estudio) y se replica aquí. El
 * test de paridad cruzada (engine.parity.test.js) lo garantiza en CI/build.
 *
 * Parámetros fijos del Pine (INTOCABLES, ver docs/research/rango_quantum_pine_source.pine):
 * EMA 21/50/200, RSI 14 (50/50, OB 75, OS 25), MACD 12/26/9, volumen >
 * 1.2×SMA20, ATR 14, SL 1.5×ATR (referencia, NUNCA usado como salida —
 * premisa "sin SL" de Marc), TP1 1.5×ATR / TP2 3×ATR (idem, solo referencia
 * del Pine — la config de SERIE congelada usa su propia estructura de TP,
 * ver CONFIG_SERIE_CONGELADA más abajo).
 */
import { ema, rma, atrWilder, rsiWilder, macd, sma, crossover, crossunder } from './pineIndicators.js'

export const EMA_FAST_LEN = 21
export const EMA_MID_LEN = 50
export const EMA_SLOW_LEN = 200
export const RSI_LEN = 14
export const RSI_BULL_LVL = 50
export const RSI_BEAR_LVL = 50
export const RSI_OB = 75
export const RSI_OS = 25
export const MACD_FAST = 12
export const MACD_SLOW = 26
export const MACD_SIG = 9
export const VOL_LEN = 20
export const VOL_MULT = 1.2
export const ATR_LEN = 14
export const SL_MULT = 1.5
export const TP1_MULT = 1.5
export const TP2_MULT = 3.0
export const WARMUP_BARS = 1000

/**
 * Config de serie CONGELADA (decisión de Marc, 2026-08-14 — ver
 * docs/research/rango_quantum_estudio.md sección 16): 4H, 5x fijo, TP
 * escalonado 5×ATR/6×ATR reparto 60/40 (cierre en el último TP), sin SL
 * técnico (liquidación como stop implícito), filtros rsi_narrow +
 * atr_min_median. Estos son los DEFAULTS de fábrica del panel de usuario
 * ("Restablecer valores Quantum" los recupera) — el usuario puede ajustar
 * SL/TPs/apalancamiento de referencia, ver constants.js.
 */
export const CONFIG_SERIE_CONGELADA = Object.freeze({
  timeframe: '4h',
  leverage: 5,
  tpDistancesAtr: [5.0, 6.0],
  tpWeights: [0.6, 0.4],
  tpRemainderPolicy: 'close_at_last',
  slMode: 'liquidation', // 'liquidation' | 'pct' | 'atr'
  liqMmr: 0.01,
  filters: ['rsi_narrow', 'atr_min_median'],
  // Fase 0.6 (decisión de Marc, 2026-08-15): 15 días naturales — mejor
  // perfil de cartera OOS (peor mes +0,53%, max DD ~0%, ratio IS/OOS 0,90;
  // ver docs/research/rango_quantum_timestop_informe.md). 0 = desactivado
  // (comportamiento idéntico a antes de esta feature).
  timeStopDays: 15,
})

/**
 * @param {{time:number, open:number, high:number, low:number, close:number, volume:number}[]} candles
 * @returns {Array<Record<string, any>>} una fila por vela, mismos campos que
 *   signals.py::compute_signal_frame (close, emaFast/Mid/Slow, rsi,
 *   macdLine/macdSignal, volOk, atr, longCond, shortCond, entryPrice,
 *   slPrice, tp1Price, tp2Price — SL/TP1/TP2 aquí son la REFERENCIA del
 *   Pine, 1.5/1.5/3×ATR, no la estructura de TP de la config de serie).
 */
export function computeSignalFrame(candles, warmupBars = WARMUP_BARS) {
  const n = candles.length
  const closes = candles.map((c) => c.close)
  const volumes = candles.map((c) => c.volume)

  const emaFast = ema(closes, EMA_FAST_LEN)
  const emaMid = ema(closes, EMA_MID_LEN)
  const emaSlow = ema(closes, EMA_SLOW_LEN)
  const rsi = rsiWilder(closes, RSI_LEN)
  const { macdLine, signalLine } = macd(closes, MACD_FAST, MACD_SLOW, MACD_SIG)
  const macdCrossUp = crossover(macdLine, signalLine)
  const macdCrossDown = crossunder(macdLine, signalLine)
  const volMa = sma(volumes, VOL_LEN)
  const atr = atrWilder(candles, ATR_LEN)

  const warmupCutoffIdx = n > warmupBars ? Math.min(warmupBars, n - 1) : -1

  const out = []
  for (let i = 0; i < n; i++) {
    const close = closes[i]
    const volOk = !Number.isNaN(volMa[i]) && volumes[i] > volMa[i] * VOL_MULT
    const macroLong = close > emaSlow[i]
    const macroShort = close < emaSlow[i]
    const trendUp = emaFast[i] > emaMid[i] && close > emaFast[i]
    const trendDown = emaFast[i] < emaMid[i] && close < emaFast[i]

    let longCond = Boolean(
      trendUp && macdCrossUp[i] && rsi[i] > RSI_BULL_LVL && rsi[i] < RSI_OB && volOk && macroLong,
    )
    let shortCond = Boolean(
      trendDown && macdCrossDown[i] && rsi[i] < RSI_BEAR_LVL && rsi[i] > RSI_OS && volOk && macroShort,
    )
    if (Number.isNaN(rsi[i]) || Number.isNaN(macdLine[i]) || Number.isNaN(emaSlow[i]) || Number.isNaN(atr[i])) {
      longCond = false
      shortCond = false
    }
    if (warmupCutoffIdx >= 0 && i < warmupCutoffIdx) {
      longCond = false
      shortCond = false
    }

    const atrVal = atr[i]
    let entryPrice = null
    let slPrice = null
    let tp1Price = null
    let tp2Price = null
    if (longCond) {
      entryPrice = close
      slPrice = close - atrVal * SL_MULT
      tp1Price = close + atrVal * TP1_MULT
      tp2Price = close + atrVal * TP2_MULT
    } else if (shortCond) {
      entryPrice = close
      slPrice = close + atrVal * SL_MULT
      tp1Price = close - atrVal * TP1_MULT
      tp2Price = close - atrVal * TP2_MULT
    }

    out.push({
      time: candles[i].time,
      close,
      emaFast: emaFast[i],
      emaMid: emaMid[i],
      emaSlow: emaSlow[i],
      rsi: rsi[i],
      macdLine: macdLine[i],
      macdSignal: signalLine[i],
      volOk,
      atr: atrVal,
      longCond,
      shortCond,
      entryPrice,
      slPrice,
      tp1Price,
      tp2Price,
    })
  }
  return out
}

/** Extrae solo las filas con señal (long o short) — un evento por fila. */
export function extractSignals(signalFrame) {
  return signalFrame
    .filter((row) => row.longCond || row.shortCond)
    .map((row) => ({
      time: row.time,
      direction: row.longCond ? 'long' : 'short',
      entryPrice: row.entryPrice,
      atr: row.atr,
    }))
}

/**
 * Niveles de TP de la config de SERIE (distinto del TP1/TP2 de referencia
 * del Pine) — mismo cálculo que simulator.py::TPStructure.price_for.
 */
export function tpLevelsForSeries(entryPrice, atrAtSignal, direction, config = CONFIG_SERIE_CONGELADA) {
  const dirn = direction === 'long' ? 1 : -1
  return config.tpDistancesAtr.map((distanceAtr) => entryPrice + dirn * distanceAtr * atrAtSignal)
}

/** Mismo modelo que simulator.py::LiquidationModel. */
export function liquidationPrice(entryPrice, leverage, direction, mmr = CONFIG_SERIE_CONGELADA.liqMmr) {
  const dirn = direction === 'long' ? 1 : -1
  const dist = Math.max(1 / leverage - mmr, 0.001)
  return dirn === 1 ? entryPrice * (1 - dist) : entryPrice * (1 + dist)
}
